import {
  AiJobSummary_Read,
  AiLoadSummary_Geofence,
  AiLoadSummary_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

function stringOrNull(value: unknown) {
  return typeof value === 'string' ? value : null;
}

/**
 * A model-reported anomaly from the AI segmentation run. The API serves findings as
 * free-form objects (they are written by the backend worker from LLM output), so parsing
 * narrows each field defensively.
 */
export class AiJobSummaryFinding {
  constructor(
    private _code: string | null,
    private _severity: string | null,
    private _summary: string | null,
    private _evidence: string | null,
    private _load_index: number | null,
  ) {}

  public static parse(raw: Record<string, unknown>) {
    return new AiJobSummaryFinding(
      stringOrNull(raw.code),
      stringOrNull(raw.severity),
      stringOrNull(raw.summary),
      // evidence has no documented shape; non-string evidence is serialized for display
      raw.evidence === undefined || raw.evidence === null
        ? null
        : (stringOrNull(raw.evidence) ?? JSON.stringify(raw.evidence)),
      typeof raw.load_index === 'number' ? raw.load_index : null,
    );
  }

  public get code() {
    return this._code;
  }

  public get severity() {
    return this._severity;
  }

  public get summary() {
    return this._summary;
  }

  public get evidence() {
    return this._evidence;
  }

  public get loadIndex() {
    return this._load_index;
  }
}

export class AiJobSummary {
  constructor(
    private _id: string,
    private _job_id: string,
    private _source: string,
    private _model: string | null,
    private _prompt_version: number | null,
    private _verdict: AiJobSummary_Read['verdict'],
    private _confidence: number | null,
    private _reasoning: string | null,
    private _checks: Record<string, unknown> | null,
    private _warnings: Record<string, unknown> | null,
    private _findings: AiJobSummaryFinding[],
    private _staging_dwell_seconds: number | null,
    private _computed_at: Dayjs | null,
    private _resummarizable: boolean,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public static parse(proto: AiJobSummary_Read) {
    return new AiJobSummary(
      proto.id,
      proto.job_id,
      proto.source,
      proto.model ?? null,
      proto.prompt_version ?? null,
      proto.verdict,
      proto.confidence ? Number(proto.confidence) : null,
      proto.reasoning ?? null,
      proto.checks ?? null,
      proto.warnings ?? null,
      (proto.findings ?? []).map((finding) => AiJobSummaryFinding.parse(finding)),
      proto.staging_dwell_seconds ?? null,
      proto.computed_at ? dayjs(proto.computed_at) : null,
      proto.resummarizable,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get jobId() {
    return this._job_id;
  }

  public get source() {
    return this._source;
  }

  public get model() {
    return this._model;
  }

  public get promptVersion() {
    return this._prompt_version;
  }

  public get verdict() {
    return this._verdict;
  }

  public get confidence() {
    return this._confidence;
  }

  public get reasoning() {
    return this._reasoning;
  }

  public get checks() {
    return this._checks;
  }

  public get warnings() {
    return this._warnings;
  }

  public get findings() {
    return this._findings;
  }

  public get stagingDwellSeconds() {
    return this._staging_dwell_seconds;
  }

  public get computedAt() {
    return this._computed_at;
  }

  public get resummarizable() {
    return this._resummarizable;
  }

  public get discardedAt() {
    return this._discarded_at;
  }
}

class AiLoadSummaryGeofence {
  constructor(
    private _name: string,
    private _circle_center: { lat: number; lon: number },
    private _circle_radius: number,
  ) {}

  public static parse(proto: AiLoadSummary_Geofence) {
    return new AiLoadSummaryGeofence(
      proto.name,
      proto.circle_center,
      proto.circle_radius,
    );
  }

  public get name() {
    return this._name;
  }

  public get circleCenter() {
    return this._circle_center;
  }

  public get circleRadius() {
    return this._circle_radius;
  }
}

export class AiLoadSummary {
  constructor(
    private _id: string,
    private _ai_job_summary_id: string,
    private _load_id: string | null,
    private _load_index: number,
    private _validated: boolean,
    private _pickup_entered_at: Dayjs,
    private _pickup_exited_at: Dayjs,
    private _dropoff_entered_at: Dayjs | null,
    private _dropoff_exited_at: Dayjs | null,
    private _loading_duration_seconds: number,
    private _unloading_duration_seconds: number | null,
    private _cycle_duration_seconds: number | null,
    private _pickup_geofence: AiLoadSummaryGeofence,
    private _dropoff_geofence: AiLoadSummaryGeofence | null,
    private _pickup_site_id: string | null,
    private _dropoff_site_id: string | null,
    private _site_change: boolean,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public static parse(proto: AiLoadSummary_Read) {
    return new AiLoadSummary(
      proto.id,
      proto.ai_job_summary_id,
      proto.load_id ?? null,
      proto.load_index,
      proto.validated,
      dayjs(proto.pickup_entered_at),
      dayjs(proto.pickup_exited_at),
      proto.dropoff_entered_at ? dayjs(proto.dropoff_entered_at) : null,
      proto.dropoff_exited_at ? dayjs(proto.dropoff_exited_at) : null,
      proto.loading_duration_seconds,
      proto.unloading_duration_seconds ?? null,
      proto.cycle_duration_seconds ?? null,
      AiLoadSummaryGeofence.parse(proto.pickup_geofence),
      proto.dropoff_geofence ? AiLoadSummaryGeofence.parse(proto.dropoff_geofence) : null,
      proto.pickup_site_id ?? null,
      proto.dropoff_site_id ?? null,
      proto.site_change,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get aiJobSummaryId() {
    return this._ai_job_summary_id;
  }

  public get loadId() {
    return this._load_id;
  }

  public get loadIndex() {
    return this._load_index;
  }

  public get validated() {
    return this._validated;
  }

  public get pickupEnteredAt() {
    return this._pickup_entered_at;
  }

  public get pickupExitedAt() {
    return this._pickup_exited_at;
  }

  public get dropoffEnteredAt() {
    return this._dropoff_entered_at;
  }

  public get dropoffExitedAt() {
    return this._dropoff_exited_at;
  }

  public get loadingDurationSeconds() {
    return this._loading_duration_seconds;
  }

  public get unloadingDurationSeconds() {
    return this._unloading_duration_seconds;
  }

  public get cycleDurationSeconds() {
    return this._cycle_duration_seconds;
  }

  public get pickupGeofence() {
    return this._pickup_geofence;
  }

  public get dropoffGeofence() {
    return this._dropoff_geofence;
  }

  public get pickupSiteId() {
    return this._pickup_site_id;
  }

  public get dropoffSiteId() {
    return this._dropoff_site_id;
  }

  public get siteChange() {
    return this._site_change;
  }

  public get discardedAt() {
    return this._discarded_at;
  }
}
